import React from "react";
import {
    Department,
    DepartmentVisibility,
    DiscountType,
} from "../../models/Event";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Link from "next/link";
import { observer } from "mobx-react-lite";
import AuthStore from "../../stores/AuthStore";
import { useCartStore } from "../../stores/CartStore";

const DepartmentListing = observer(
    ({
        dept,
        cartUrl,
        eventId,
        onAddToCart,
        clientId,
        emsClientIds,
        showAll = false,
    }: {
        dept: Department;
        cartUrl: string;
        eventId: number;
        onAddToCart: (comp: boolean) => void;
        clientId: number;
        emsClientIds: number[];
        showAll?: boolean;
    }) => {
        const cartStore = useCartStore();

        const canAddToCart =
            (AuthStore.boxoffice &&
                AuthStore.hasBoxofficeAddToCartForDepartment(
                    emsClientIds,
                    dept.id,
                )) ||
            !AuthStore.boxoffice;

        const canAddCompToCart =
            AuthStore.boxoffice &&
            AuthStore.hasBoxofficeAddToCartAsCOMPForDepartment(
                emsClientIds,
                dept.id,
            );

        const visibility = AuthStore.boxoffice
            ? dept.boxofficeVisibility
            : dept.ecommerceVisibility;

        const metadata = dept.metadata ?? {};

        let overridePrice: number | null = null;
        const showOriginalPrice = !!dept.discount?.minItems;
        if (dept.discount) {
            if (
                !dept.discount.maxUses ||
                dept.discount.maxUses > dept.discount.uses
            ) {
                switch (dept.discount.type) {
                    case DiscountType.DOLLAR:
                        overridePrice = dept.price - dept.discount.value * 100;
                        break;
                    case DiscountType.PERCENTAGE:
                        overridePrice =
                            dept.price -
                            dept.price * (dept.discount.value / 100);
                        break;
                    case DiscountType.TIERED:
                        const uses =
                            dept.discount.uses +
                            (cartStore?.getDiscountUses(dept.discount.id) ?? 0);
                        for (let da of dept.discount.amounts.sort(
                            (a, b) => b.minQuantity - a.minQuantity,
                        ) ?? []) {
                            if (
                                (da.minQuantity <= uses &&
                                    da.maxQuantity > uses) ||
                                da.uses +
                                    (cartStore?.getDiscountAmountUses(da.id) ??
                                        0) <
                                    da.maxQuantity - da.minQuantity
                            ) {
                                switch (da.type) {
                                    case DiscountType.DOLLAR:
                                        overridePrice =
                                            dept.price - da.value * 100;
                                        break;
                                    case DiscountType.PERCENTAGE:
                                        overridePrice =
                                            dept.price -
                                            dept.price * (da.value / 100);
                                        break;
                                    default:
                                        break;
                                }
                            }
                        }
                }
            }
        }

        if (overridePrice) {
            overridePrice = Math.max(0, overridePrice);
        }

        const basePrice = dept.price;

        const serviceFeeTotal = dept.serviceFees.reduce(
            (a, b) => a + b.value,
            0,
        );

        let priceString =
            metadata.displayPrice ??
            (dept.price !== 0
                ? `$${((dept.price + serviceFeeTotal) / 100).toFixed(2)}${
                      dept.assignedSeating ? " per Seat" : ""
                  }`
                : "FREE");

        let breakdownString =
            basePrice !== 0 && basePrice !== basePrice + serviceFeeTotal
                ? ` ($${basePrice / 100} + $${serviceFeeTotal / 100} fees)`
                : "";

        let overridePriceString =
            overridePrice !== null
                ? `$${(overridePrice / 100).toFixed(2)}${
                      dept.assignedSeating ? " per Seat" : ""
                  }`
                : "";

        const nameString = metadata.displayName ?? dept.name;

        const buttonName = metadata.buttonName ?? "View Seats";

        return (
            <Box sx={{ height: "100%" }}>
                <Card sx={{ height: "100%" }}>
                    <CardContent sx={{ height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                minHeight: "100px",
                                height: "100%",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                        >
                                            {nameString}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            minWidth: "150px",
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                textAlign: "right",
                                                fontWeight: 600,
                                            }}
                                            variant="body2"
                                        >
                                            {priceString}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                textAlign: "right",
                                                fontWeight: 500,
                                            }}
                                            variant="body2"
                                        >
                                            {breakdownString}
                                        </Typography>
                                        {/* {overridePrice !== null && (
                                            <Typography
                                                sx={{
                                                    textAlign: "right",
                                                    fontWeight: 600,
                                                    color: "success.light",
                                                }}
                                                variant="body2"
                                            >
                                                {dept.discount?.minItems
                                                    ? `${dept.discount.minItems} or more: `
                                                    : ""}
                                                {overridePriceString
                                                    ? overridePriceString
                                                    : ""}
                                            </Typography>
                                        )} */}
                                    </Box>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    component="div"
                                    dangerouslySetInnerHTML={{
                                        __html: dept.details,
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                {dept.soldOut ? (
                                    <Chip color="error" label="SOLD OUT" />
                                ) : (
                                    <>
                                        {(visibility ===
                                            DepartmentVisibility.OPEN ||
                                            showAll) && (
                                            <Box
                                                sx={{
                                                    mt: {
                                                        xs: 1,
                                                        sm: 0,
                                                    },
                                                }}
                                            >
                                                {dept.assignedSeating ? (
                                                    <Link
                                                        href={`/store/${cartUrl}/${eventId}/${dept.id}`}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                        >
                                                            {buttonName}
                                                        </Button>
                                                    </Link>
                                                ) : (
                                                    <>
                                                        {canAddCompToCart && (
                                                            <Button
                                                                onClick={() =>
                                                                    onAddToCart(
                                                                        true,
                                                                    )
                                                                }
                                                                sx={{ mr: 1 }}
                                                                variant="contained"
                                                                size="small"
                                                            >
                                                                Add COMP to Cart
                                                            </Button>
                                                        )}
                                                        {canAddToCart && (
                                                            <Button
                                                                onClick={() =>
                                                                    onAddToCart(
                                                                        false,
                                                                    )
                                                                }
                                                                variant="contained"
                                                                size="small"
                                                            >
                                                                Add to Cart
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    },
);

export default DepartmentListing;
